import type { AssetBookingAddonOption } from './bookingAssetAddons';
import { normalizeBookingCreateAddons } from '../api/bookingCreatePayload';
import type { AssetBookingPricingOption, BookingPeriodKind } from './assetBookingPricingOptions';
import { addBookingPeriods, computeBookingTotalPrice } from './bookingPeriodEndDate';
import { bookingRecordPrimaryId, coerceBookingWireTotalPrice } from './bookingRecordMutate';
import { loadBookingPricingTypePreference } from './bookingPricingTypeStorage';

const PERIOD_KIND_RANK: Record<BookingPeriodKind, number> = {
  hour: 1,
  day: 2,
  week: 3,
  month: 4,
  year: 5,
};

/** Max |computedEnd − actualEnd| to treat a period kind as matching the booking span. */
function scheduleMatchToleranceMs(kind: BookingPeriodKind): number {
  if (kind === 'hour') {
    return 60 * 60 * 1000;
  }
  return 24 * 60 * 60 * 1000;
}

const MAX_PERIOD_SEARCH = 5000;

/**
 * How many whole booking periods fit between start and end (same rules as {@link addBookingPeriods}).
 */
export function countBookingPeriodsBetween(
  startIso: string,
  endIso: string,
  kind: BookingPeriodKind,
): number | null {
  const start = new Date(startIso.trim());
  const end = new Date(endIso.trim());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    return null;
  }

  let lo = 1;
  let hi = MAX_PERIOD_SEARCH;
  let best = 0;

  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    const endCandidate = addBookingPeriods(startIso, kind, mid);
    if (!endCandidate) {
      return null;
    }
    const endMs = new Date(endCandidate).getTime();
    if (endMs <= end.getTime()) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  return best >= 1 ? best : null;
}

export function sumSelectedBookingAddonsPrice(
  addons: unknown,
  choices: AssetBookingAddonOption[],
): number {
  const rows = normalizeBookingCreateAddons(addons);
  if (rows.length === 0) {
    return 0;
  }
  const byId = new Map(choices.map((c) => [c.addonId, c]));
  let sum = 0;
  for (const row of rows) {
    const opt = byId.get(row.addon_id);
    if (opt?.price !== null && opt?.price !== undefined && Number.isFinite(opt.price)) {
      sum += opt.price * row.quantity;
    }
  }
  return sum;
}

function coerceNumericId(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function coercePricingTypeIdFromRecord(record: Record<string, unknown>): number | null {
  const raw =
    record.pricing_type_id ??
    record.pricingTypeId ??
    record.pricing_method_id ??
    record.pricingMethodId;
  const fromFields = coerceNumericId(raw);
  if (fromFields !== null) {
    return fromFields;
  }
  const bookingId = bookingRecordPrimaryId(record);
  if (bookingId) {
    return loadBookingPricingTypePreference(bookingId);
  }
  return null;
}

/**
 * When the API omits `pricing_type_id`, infer the plan from start/end (prefer month over day when both fit).
 */
export function inferBookingPricingOptionFromSchedule(
  options: AssetBookingPricingOption[],
  startIso: string,
  endIso: string,
  totalPrice?: number | null,
): AssetBookingPricingOption | null {
  const start = startIso.trim();
  const end = endIso.trim();
  if (!start || !end) {
    return null;
  }
  const endMs = new Date(end).getTime();
  if (Number.isNaN(endMs)) {
    return null;
  }

  type Candidate = {
    option: AssetBookingPricingOption;
    periodCount: number;
    endDeltaMs: number;
    rank: number;
  };
  const candidates: Candidate[] = [];

  for (const option of options) {
    if (!option.kind) {
      continue;
    }
    const kind = option.kind;
    const periodCount = countBookingPeriodsBetween(start, end, kind);
    if (periodCount === null) {
      continue;
    }
    const computedEnd = addBookingPeriods(start, kind, periodCount);
    if (!computedEnd) {
      continue;
    }
    const endDeltaMs = Math.abs(new Date(computedEnd).getTime() - endMs);
    if (endDeltaMs > scheduleMatchToleranceMs(kind)) {
      continue;
    }
    candidates.push({
      option,
      periodCount,
      endDeltaMs,
      rank: PERIOD_KIND_RANK[kind],
    });
  }

  if (candidates.length === 0) {
    return null;
  }

  const wireTotal = totalPrice !== undefined && totalPrice !== null ? coerceBookingWireTotalPrice(totalPrice) : null;

  candidates.sort((a, b) => {
    if (wireTotal !== null && a.option.price !== null && b.option.price !== null) {
      const aTotal = coerceBookingWireTotalPrice(a.option.price * a.periodCount);
      const bTotal = coerceBookingWireTotalPrice(b.option.price * b.periodCount);
      const aDiff = aTotal !== null ? Math.abs(aTotal - wireTotal) : Number.MAX_SAFE_INTEGER;
      const bDiff = bTotal !== null ? Math.abs(bTotal - wireTotal) : Number.MAX_SAFE_INTEGER;
      if (aDiff !== bDiff) {
        return aDiff - bDiff;
      }
    }
    if (a.endDeltaMs !== b.endDeltaMs) {
      return a.endDeltaMs - b.endDeltaMs;
    }
    return b.rank - a.rank;
  });

  return candidates[0].option;
}

/** Pick pricing row for update: saved type id, else sole option, else first with a known period kind. */
export function resolveBookingPricingOptionForUpdate(
  options: AssetBookingPricingOption[],
  record: Record<string, unknown>,
): AssetBookingPricingOption | null {
  if (options.length === 0) {
    return null;
  }
  const fromRecord = coercePricingTypeIdFromRecord(record);
  if (fromRecord !== null) {
    const match = options.find((o) => o.pricingTypeId === fromRecord);
    if (match) {
      return match;
    }
  }

  const startIso = typeof record.start_date === 'string' ? record.start_date : '';
  const endIso = typeof record.end_date === 'string' ? record.end_date : '';
  const totalPrice = coerceBookingWireTotalPrice(record.total_price);
  if (startIso && endIso) {
    const inferred = inferBookingPricingOptionFromSchedule(options, startIso, endIso, totalPrice);
    if (inferred) {
      return inferred;
    }
  }

  if (options.length === 1) {
    return options[0];
  }
  const withKind = options.filter((o) => o.kind);
  if (withKind.length === 1) {
    return withKind[0];
  }
  return withKind[0] ?? options[0];
}

export type ComputeBookingWireTotalParams = {
  startIso: string;
  endIso: string;
  pricingOption: AssetBookingPricingOption | null;
  addons: unknown;
  addonChoices: AssetBookingAddonOption[];
};

/** Base period price + add-on lines, rounded to API integer ≥ 1. */
export function computeBookingWireTotalPrice(params: ComputeBookingWireTotalParams): number | null {
  const { startIso, endIso, pricingOption, addons, addonChoices } = params;
  if (!pricingOption?.kind || pricingOption.price === null) {
    const addonOnly = sumSelectedBookingAddonsPrice(addons, addonChoices);
    if (addonOnly > 0) {
      return coerceBookingWireTotalPrice(addonOnly);
    }
    return null;
  }
  const periodCount = countBookingPeriodsBetween(startIso, endIso, pricingOption.kind);
  if (periodCount === null) {
    return null;
  }
  const base = computeBookingTotalPrice(pricingOption.price, periodCount);
  if (base === null) {
    return null;
  }
  const total = base + sumSelectedBookingAddonsPrice(addons, addonChoices);
  return coerceBookingWireTotalPrice(total);
}
